// WHERE A RUN IS: the one verdict a run pulse carries (#5918), and the idle rule that
// is one of its proofs.
//
// The verdict itself is computed once, at the producer (`readTaskRunStatus`,
// `apps/api/src/deps/mcp-ports.ts`), from the run's newest durable event. What lives
// here is what the producer, the wire and the SSE pump must agree on and cannot import
// from each other: the vocabulary and the silence horizon.

import { z } from 'zod';

/**
 * How long a run may go without emitting ANYTHING before it is over by evidence.
 *
 * `run_finished` cannot be the only terminal proof: 4,287 of 7,139 prod runs
 * (60%, 2026-08-26) never emit one, so `done` was unreachable for the majority
 * and every one of them tailed to the SSE duration cap, said `bye`, and had the
 * dashboard reconnect — one `eventsAfter` per second and one NATS subscription
 * per open tab, for ever, on a run that ended weeks ago.
 *
 * The horizon is MEASURED, not guessed. Across all 7,140 prod runs the largest
 * gap between two consecutive events of one run is 1,715s (~28.6 min); four runs
 * ever exceeded 15 minutes and none exceeded 30. A live box also beats every
 * `DZ_RUN_HEARTBEAT_MS` (60s default, `apps/runner/src/guards.ts`), so half an
 * hour of silence is thirty missed beats. The cost of being wrong is bounded and
 * visible: the view says "Ended" and its Retry re-opens the stream.
 */
export const DEFAULT_IDLE_MS = 30 * 60_000;

/**
 * "This run is over, whatever it emitted" — the newest event is at least `idleMs`
 * old. See {@link DEFAULT_IDLE_MS} for the measurement.
 *
 * `null` (nothing observed yet) is never idle: absence of a row is not evidence
 * about a run, and neither is a `NaN` timestamp — both fall through rather than
 * declaring an end this function cannot prove.
 */
export function runIsIdle(lastEventAt: number | null, now: number, idleMs: number): boolean {
  if (lastEventAt === null || !Number.isFinite(lastEventAt)) return false;
  return now - lastEventAt >= idleMs;
}

/**
 * Where a run is, as ONE answer — the field a consumer renders. Never derive
 * liveness from a boolean beside it: `!finished` called a released, an errored and
 * a long-silent run live, which is the defect this vocabulary exists to end.
 *
 *   `live`      none of the below: the run may still speak.
 *   `finished`  its newest event is the `run_finished` sign-off.
 *   `errored`   its newest event is an `error` — the attempt failed.
 *   `released`  its newest event is `run_released` — the box gave the claim back.
 *   `idle`      no terminal event, and silent for {@link DEFAULT_IDLE_MS}.
 *
 * The three terminals outrank silence: a run that errored a week ago is `errored`.
 */
export const TASK_RUN_STATES = ['live', 'finished', 'errored', 'released', 'idle'] as const;
export const TaskRunStateSchema = z.enum(TASK_RUN_STATES);
export type TaskRunState = z.infer<typeof TaskRunStateSchema>;
